// ./src/components/features/WorkflowTable/WorkflowSelector.tsx
import React, { useEffect, useMemo } from "react";
import { Select, SelectProps } from "@mantine/core";
import { STATUSES } from "@sdflc/utils";
import { useWorkflows } from "@/hooks";
import { LoadingIndicator } from "@/components/generic";
import { Workflow } from "@/utils/api";
import { useApp } from "@/contexts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Change event emitted by WorkflowSelector.
 *
 * Mirrors the shape used by other inputs in the codebase so a parent
 * component can use a single generic change handler for all its fields:
 *
 *   const handleChange = ({ name, value }) => setForm(prev => ({ ...prev, [name]: value }));
 */
export interface WorkflowSelectorChangeEvent {
  /** The `name` prop passed to the selector — identifies the field */
  name: string;
  /** Selected workflow ID, or null when cleared */
  value: string | null;
  /** Full Workflow object for the selected id, or undefined when cleared */
  workflow?: Workflow;
}

export interface WorkflowSelectorProps extends Omit<
  SelectProps,
  "data" | "value" | "onChange"
> {
  /**
   * Field name — returned as-is in the change event so the parent can use
   * a single handler for multiple inputs.
   */
  name: string;

  /** Currently selected workflow ID */
  value: string | null | undefined;

  /** Called whenever the selection changes or is cleared */
  onChange?: (event: WorkflowSelectorChangeEvent) => void;

  /**
   * When true the selector is treated as mandatory:
   *   • The first enabled option is auto-selected after load if value is empty.
   *   • The Select is rendered without a clear button (clearable=false).
   */
  required?: boolean;

  /**
   * When true, inactive workflows are included in the list.
   * Defaults to false — only ACTIVE workflows are shown.
   */
  includeInactive?: boolean;

  /**
   * Optional predicate to further narrow the displayed options after the
   * active/inactive filter has been applied.
   */
  workflowFilter?: (workflow: Workflow) => boolean;

  /** Placeholder shown when nothing is selected */
  placeholder?: string;

  /** Text shown while workflows are loading */
  loadingPlaceholder?: string;

  /** Text shown when the list is empty after loading */
  emptyText?: string;

  /** Error message — passed straight through to Select */
  error?: React.ReactNode;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Component size — defaults to "md" */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Any additional SelectProps not listed above */
  [key: string]: any;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const WorkflowSelector: React.FC<WorkflowSelectorProps> = ({
  name,
  value,
  onChange,
  required = false,
  includeInactive = false,
  workflowFilter,
  placeholder = "Select a workflow",
  loadingPlaceholder = "Loading workflows...",
  emptyText = "No workflows available",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { user } = useApp();

  const { workflows, loading, isEmpty, lastError } = useWorkflows({
    autoLoad: true,
    userId: user?.id,
    // Load active-only by default; caller opts in to inactive if needed
    initialFilters: includeInactive ? {} : { status: [STATUSES.ACTIVE] },
  });

  // ------------------------------------------------------------------
  // Build a stable id→Workflow map for O(1) lookups on change and for
  // the auto-select effect. Recomputed only when the workflows array
  // reference changes (i.e. when new data arrives from the hook).
  // ------------------------------------------------------------------
  const workflowMap = useMemo(() => {
    const map = new Map<string, Workflow>();
    for (const wf of workflows) {
      if (wf.id) {
        map.set(wf.id, wf);
      }
    }
    return map;
  }, [workflows]);

  // ------------------------------------------------------------------
  // Build Select options from the (optionally filtered) workflow list.
  // Inactive items are rendered as disabled options so the user can see
  // them in the list but cannot select them.
  // ------------------------------------------------------------------
  const options = useMemo(() => {
    let filtered = workflows;

    if (workflowFilter) {
      filtered = filtered.filter(workflowFilter);
    }

    return filtered.map((wf) => ({
      value: wf.id ?? "",
      label: wf.workflowName ?? wf.id ?? "Unnamed workflow",
      disabled: wf.status !== STATUSES.ACTIVE,
    }));
  }, [workflows, workflowFilter]);

  // ------------------------------------------------------------------
  // Auto-select — fires when:
  //   • required is true
  //   • no value is currently selected
  //   • loading has finished
  //   • at least one enabled option exists
  //
  // Picks the first non-disabled option and emits an onChange event
  // identical to what a manual selection would produce, so the parent
  // does not need any special handling for this case.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!required || value || loading || !onChange) {
      return;
    }

    const firstEnabled = options.find((opt) => !opt.disabled);
    if (!firstEnabled) {
      return;
    }

    onChange({
      name,
      value: firstEnabled.value,
      workflow: workflowMap.get(firstEnabled.value),
    });
  }, [required, value, loading, options, onChange, name, workflowMap]);

  // ------------------------------------------------------------------
  // Change handler — wraps Mantine's string | null into the richer event
  // shape so parents can use a single generic handler for all fields.
  // ------------------------------------------------------------------
  const handleChange = (selectedId: string | null) => {
    if (!onChange) {
      return;
    }

    onChange({
      name,
      value: selectedId,
      workflow: selectedId ? workflowMap.get(selectedId) : undefined,
    });
  };

  // ------------------------------------------------------------------
  // Derive Select state from loading / error / empty conditions.
  // ------------------------------------------------------------------
  const isInitialLoading = loading && workflows.length === 0;
  const hasError = !!lastError && !loading;
  const isEmptyState = isEmpty && !loading;

  // Error message — prefer the caller's own error prop (field-level error
  // from a parent form), then fall back to the load error summary.
  const errorMessage: React.ReactNode =
    error ??
    (hasError
      ? lastError?.getErrorSummary() || "Failed to load workflows"
      : undefined);

  // Placeholder adapts to the current state so the user always knows why
  // the control looks the way it does.
  const activePlaceholder = isInitialLoading
    ? loadingPlaceholder
    : hasError
      ? "Error loading workflows"
      : isEmptyState
        ? emptyText
        : placeholder;

  // The selector is non-interactive while loading, errored, or empty.
  const isDisabled = disabled || isInitialLoading || hasError || isEmptyState;

  // Show a spinner in the right section while any load is in flight
  // (both initial load and background refreshes while items exist).
  const rightSection = loading ? (
    <LoadingIndicator size={size} color="gray" />
  ) : (
    selectProps.rightSection
  );

  return (
    <Select
      {...selectProps}
      name={name}
      size={size}
      data={isInitialLoading || hasError || isEmptyState ? [] : options}
      value={value ?? null}
      onChange={handleChange}
      placeholder={activePlaceholder}
      // Required fields cannot be cleared — the auto-select ensures a
      // value is always present once the list loads.
      clearable={!required}
      searchable
      required={required}
      disabled={isDisabled}
      error={errorMessage}
      rightSection={rightSection}
    />
  );
};
